import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCriarValorRecuperacao, useValoresRecuperacao } from '@/api/fichas';
import { avisarDesfazer, HistoricoRegistro } from '@/components/desfazer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { decimalDigitado, formatarCusto, formatarData } from '@/lib/format';
import { hojeIso } from './tributos-item';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const AJUDA = 'text-caption text-ink-muted';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface RecuperacaoItemProps {
  itemId: string;
  /** Pode registrar nova vigencia (custoAquisicao.editar + custo.ver). Sem a permissao a aba so mostra as vigencias. */
  podeRegistrar: boolean;
}

/**
 * Aba "Valor de recuperacao" do item: quanto se recebe por unidade quando o item sai de outra ficha como subproduto.
 * Lista as vigencias (GET /valores-recuperacao?itemId=) e grava uma nova (POST /valores-recuperacao). Nenhum R$ e calculado aqui.
 */
export function RecuperacaoItem({ itemId, podeRegistrar }: RecuperacaoItemProps) {
  const lista = useValoresRecuperacao({ itemId, ativo: true });
  const criar = useCriarValorRecuperacao();
  const qc = useQueryClient();
  const [validFrom, setValidFrom] = useState(() => hojeIso());
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function gravar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);
    setOk(null);
    if (!validFrom) {
      setErro('Informe a data a partir da qual o valor vale.');
      return;
    }
    // So formato: texto pt-BR vira decimal com ponto (sem conta). Vazio ou invalido nao vai para a API.
    const decimal = decimalDigitado(valor);
    if (decimal === null) {
      setErro('Informe o valor de recuperação por unidade (R$).');
      return;
    }
    try {
      const gravada = await criar.mutateAsync({ itemId, validFrom, valor: decimal, ...(motivo.trim() ? { motivo: motivo.trim() } : {}) });
      void avisarDesfazer({ recurso: 'valores_recuperacao', chave: itemId, qc });
      setOk(`Vigência gravada a partir de ${formatarData(gravada.validFrom)}. A anterior foi encerrada nessa data.`);
      setValor('');
      setMotivo('');
    } catch (err) {
      setErro(mensagemErro(err));
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="flex justify-end">
        <HistoricoRegistro recurso="valores_recuperacao" chave={itemId} />
      </div>
      <p className={AJUDA}>
        O valor de recuperação é quanto se recebe por unidade deste item quando ele sai de outra ficha como subproduto. Vale por vigência; a nova vigência fecha a anterior.
      </p>

      {lista.isError ? (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {mensagemErro(lista.error)}
        </div>
      ) : !lista.data ? (
        <p role="status" className="text-small text-ink-muted">
          Carregando vigências…
        </p>
      ) : lista.data.length === 0 ? (
        <p role="status" className="text-small text-ink-muted">
          Nenhum valor de recuperação cadastrado para este item.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] border-collapse text-small" aria-label="Vigências do valor de recuperação">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="py-2 pr-3 font-medium">Vigência (de/até)</th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">R$ por unidade</th>
                <th scope="col" className="py-2 pr-3 font-medium">Hipotético</th>
                <th scope="col" className="py-2 font-medium">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {lista.data.map((v) => (
                <tr key={v.id} className="border-t border-line-soft" data-testid="valor-recuperacao">
                  <td className="py-2 pr-3">
                    {formatarData(v.validFrom)} · {v.validTo ? `até ${formatarData(v.validTo)}` : 'vigente'}
                  </td>
                  <td className="tabular py-2 pr-3 text-right">{formatarCusto(v.valor)}</td>
                  <td className="py-2 pr-3">
                    {v.hipotetico ? (
                      <span className="rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink" data-testid="recuperacao-hipotetico">
                        Hipotético
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2">{v.motivo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {podeRegistrar ? (
        <form onSubmit={(e) => void gravar(e)} noValidate aria-label="Nova vigência do valor de recuperação" className="grid gap-3.5 rounded-xl border border-border p-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="rec-data" className={ROTULO}>
              Vale a partir de
            </Label>
            <Input id="rec-data" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="rec-valor" className={ROTULO}>
              Valor R$ por unidade
            </Label>
            <Input id="rec-valor" inputMode="decimal" className="font-mono" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="rec-motivo" className={ROTULO}>
              Motivo (opcional)
            </Label>
            <Input id="rec-motivo" maxLength={300} placeholder="Ex.: nova cotação da sucata" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2 sm:col-span-3">
            {erro && (
              <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
                {erro}
              </div>
            )}
            {ok && (
              <p role="status" className="text-small text-success-ink">
                {ok}
              </p>
            )}
            <div>
              <button type="submit" disabled={criar.isPending} className="h-9 rounded-[10px] bg-primary px-3.5 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {criar.isPending ? 'Gravando…' : 'Gravar valor'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <p role="status" className="text-small text-ink-muted">
          Sem permissão para registrar o valor de recuperação; mostramos só as vigências.
        </p>
      )}
    </div>
  );
}
